import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { turnStatusKey } from '../constants';
import { AgentForgeApiError, callAgentForge } from './http';
import type {
  TurnCancellationTarget,
  TurnStatus,
  TurnStatusResponse,
  UseTurnStatusArgs,
} from './turnStatus.types';

const STATUS_ALIASES: Record<string, TurnStatus> = {
  idle: 'idle',
  queued: 'running',
  pending: 'running',
  accepted: 'running',
  processing: 'running',
  in_progress: 'running',
  running: 'running',
  cancelling: 'cancelling',
  succeeded: 'completed',
  complete: 'completed',
  completed: 'completed',
  error: 'failed',
  failed: 'failed',
  canceled: 'cancelled',
  cancelled: 'cancelled',
  timeout: 'timed_out',
  timed_out: 'timed_out',
};

const DISCOVERY_RETRY_MS = 5_000;

const errorStatus = (error: unknown): number | undefined => {
  if (error instanceof AgentForgeApiError) return error.status;
  const status = (error as { response?: { status?: unknown } } | undefined)
    ?.response?.status;
  return typeof status === 'number' ? status : undefined;
};

const isStructuredTurnNotFound = (error: unknown): boolean =>
  error instanceof AgentForgeApiError &&
  error.status === 404 &&
  error.errors.some((message) =>
    message.toLowerCase().includes('turn not found')
  );

export const requestTurnCancellation = (
  target: TurnCancellationTarget,
  signal?: AbortSignal
): Promise<unknown> => {
  const exact =
    target.turnId && target.identityMode !== 'legacy'
      ? `/sessions/${target.sessionId}/turns/${target.turnId}/cancel`
      : null;
  // Never downgrade an exact/probing Stop to the session-wide endpoint after
  // a 404: that stale Stop could cancel a newer turn. Legacy cancellation is
  // used only for persisted/acknowledged records known to lack exact identity.
  return callAgentForge({
    method: 'POST',
    url: exact ?? `/sessions/${target.sessionId}/cancel`,
    signal,
  });
};

export const normalizeTurnStatus = (value: unknown): TurnStatus => {
  if (typeof value !== 'string') return 'unknown';
  return STATUS_ALIASES[value.trim().toLowerCase()] ?? 'unknown';
};

const normalizedResponse = (
  value: unknown,
  identityMode: 'exact' | 'legacy'
): TurnStatusResponse | null => {
  if (!value || typeof value !== 'object') return null;
  const body = value as TurnStatusResponse;
  return {
    ...body,
    status: normalizeTurnStatus(body.status),
    identity_mode: identityMode,
  };
};

const getSessionTurnStatus = async (
  sessionId: string,
  signal?: AbortSignal
): Promise<TurnStatusResponse> => {
  const body = await callAgentForge<unknown>({
    url: `/sessions/${sessionId}/turn-status`,
    signal,
  });
  if (!body || typeof body !== 'object') {
    throw new Error('invalid turn-status response');
  }
  const response = body as TurnStatusResponse;
  return {
    ...response,
    status: normalizeTurnStatus(response.status),
    identity_mode: response.turn_id ? 'exact' : 'legacy',
  };
};

const getTurnStatus = async (
  args: UseTurnStatusArgs,
  signal?: AbortSignal
): Promise<TurnStatusResponse> => {
  const sessionId = args.sessionId ?? '';
  const useExact = !!args.turnId && args.identityMode !== 'legacy';
  if (!useExact) return getSessionTurnStatus(sessionId, signal);

  try {
    const body = await callAgentForge<unknown>({
      url: `/sessions/${sessionId}/turns/${args.turnId}`,
      signal,
    });
    const response = normalizedResponse(body, 'exact');
    if (response) return response;
    // Older servers can resolve an unknown Chi route as a plain-text 404.
    // Only an unconfirmed client turn may probe the legacy read path.
    if (args.identityMode === 'probing') {
      return getSessionTurnStatus(sessionId, signal);
    }
    throw new Error('invalid exact turn-status response');
  } catch (error) {
    if (isStructuredTurnNotFound(error)) {
      // Synthetic idle is scoped to this exact UUID. The recovery hook keeps
      // polling through the POST visibility grace, then safely unlocks if the
      // durable acceptance row never appears.
      return {
        turn_id: args.turnId,
        status: 'idle',
        identity_mode: 'exact',
      };
    }
    if (errorStatus(error) === 404 && args.identityMode === 'probing') {
      const fallback = await getSessionTurnStatus(sessionId, signal);
      if (fallback.turn_id && fallback.turn_id !== args.turnId) {
        return {
          turn_id: args.turnId,
          status: 'idle',
          identity_mode: 'exact',
        };
      }
      return fallback;
    }
    throw error;
  }
};

export const useTurnStatusQuery = (
  args: UseTurnStatusArgs
): UseQueryResult<TurnStatusResponse, Error> => {
  const sessionId = args.sessionId ?? '';
  const identityMode = args.identityMode;
  const exactTurnId = identityMode === 'legacy' ? undefined : args.turnId;
  return useQuery({
    queryKey: turnStatusKey(sessionId, exactTurnId, identityMode),
    queryFn: ({ signal }) =>
      getTurnStatus(
        {
          sessionId,
          turnId: exactTurnId,
          identityMode,
        },
        signal
      ),
    enabled: (args.enabled ?? true) && sessionId.length > 0,
    staleTime: 0,
    retry: false,
    // A deployment may make the very first status probe fail before this
    // browser has any local active-turn state. Keep discovering until one
    // successful response lands; after that, idle sessions stop polling while
    // active sessions supply their fixed interval through the hook argument.
    refetchInterval: (query) =>
      args.refetchIntervalMs ??
      (query.state.data === undefined ? DISCOVERY_RETRY_MS : false),
  });
};
